/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import ReceiverList from "./components/ReceiverList";
import ReceiverSearch from "./components/ReceiverSearch";
import { useNavigate } from "react-router-dom";
import ReceiverToolbar from "./components/ReceiverToolbar";

interface Filters {
  [key: string]: any;
}

const rowPerPage = 10;

const initialFilters: Filters = {
  field: "",
  keyword: "",
  status: "",
  kyc_status: "",
  merchant_id: "",
  subaccount_id: "",
};

interface PayloadType {
  [key: string]: any;
}

const Receiver = ({ userPermissions }: { userPermissions: any }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFilterLoading, setIsFilterLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [lastKeys, setLastKeys] = useState<{ [key: number]: string }>({});
  const [currentLastKey, setCurrentLastKey] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowPerPage);
  const [rowCount, setRowCount] = useState(rowPerPage);
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  // NEW: control filter toggle
  const [showFilters, setShowFilters] = useState(false);

  // AbortController for cancelling requests on unmount
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  // Cleanup: Cancel all pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create new AbortController for each batch of requests
  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  useEffect(() => {
    const signal = getAbortSignal();
    const currentRequestId = ++requestIdRef.current;

    const fetchData = async () => {
      localStorage.removeItem("merchantIdToFilterReceiver");
      localStorage.removeItem("subaccountIdToFilterReceiver");
      setIsLoading(true);
      await fetchReceivers({
        page: 0,
        limit: rowPerPage,
        lastkey: "",
        ...filters,
      }, signal, currentRequestId);
      if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      setIsLoading(false);
      setIsFilterLoading(false);
    };

    fetchData();
  }, []);

  const fetchReceivers = async (payload: PayloadType = {}, signal?: AbortSignal, requestId?: number) => {
    try {
      const queryObject: PayloadType = {
        limit: payload?.limit,
        lastkey: payload?.lastkey,
      };

      if (
        payload?.field !== "" &&
        payload?.field !== undefined &&
        payload?.keyword !== "" &&
        payload?.keyword !== undefined
      ) {
        queryObject[payload?.field] = payload?.keyword;
      }

      if (payload?.status !== "" && payload?.status !== undefined) {
        queryObject.status = payload?.status;
      }

      if (payload?.kyc_status !== "" && payload?.kyc_status !== undefined) {
        queryObject.kyc_status = payload?.kyc_status;
      }

      if (userRole === "superadmin") {
        const merchantId = localStorage.getItem("merchantIdToFilterReceiver");
        const subaccountId = localStorage.getItem(
          "subaccountIdToFilterReceiver"
        );

        if (merchantId && merchantId !== "" && merchantId !== undefined) {
          queryObject.merchant_id = merchantId;
        }

        if (subaccountId && subaccountId !== "" && subaccountId !== undefined) {
          queryObject.subaccount_id = subaccountId;
        }
      }

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/receiver/func/get-receivers?${queryString}`,
        { signal }
      );

      // Don't update state if request was aborted
      if (signal?.aborted) return;
      if (requestId !== undefined && requestIdRef.current !== requestId) return;

      let newData = response.data.receivers?.payload;

      if (newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }

      // Manage Last Key and Total Record
      let newLastKey = response.data.receivers?.lastkey;
      if (newLastKey) {
        setCurrentLastKey(newLastKey);
        let newPage = payload?.page;
        let newPageSize = payload?.limit;

        if (newData.length !== 0) {
          setRowCount((newPage + 1) * newPageSize + newPageSize);
        } else {
          setRowCount(newPage * newPageSize + newPageSize);
        }
        setLastKeys({ ...lastKeys, [newPage]: newLastKey });
      }
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      if (requestId !== undefined && requestIdRef.current !== requestId) return;
      setTableData([]);
      // Clear all
      setPage(0);
      setPageSize(0);
      setRowCount(0);
      setLastKeys([]);
      setCurrentLastKey("");
      console.error("Error fetching data", error);
    }
  };

  const setPaginationModel = async (newValue: any) => {
    setIsLoading(true);

    let newFilters: any = {
      page: newValue.page,
      limit: newValue.pageSize,
      lastkey: currentLastKey,
      ...filters,
    };

    // If click prev pagination
    if (newValue.page < page) {
      newFilters = {
        ...newFilters,
        lastkey: newValue.page !== 0 ? lastKeys[newValue.page - 1] : "",
      };
    }

    // if select new row per page, reset page to 0, reset lastkey
    if (newValue.pageSize !== pageSize) {
      newFilters = {
        page: 0,
        limit: newValue.pageSize,
        lastkey: "",
        ...filters,
      };
      setPage(0);
      setPageSize(newValue.pageSize);
      setCurrentLastKey("");
      setLastKeys([]);
    } else {
      setPage(newValue.page);
    }

    await fetchReceivers(newFilters);
    setIsLoading(false);
  };

  const handleClickCreate = () => {
    navigate(`/${userRole}/receiver/create`);
  };

  const handleSearchSubmit = async () => {
    setIsLoading(true);
    setIsFilterLoading(true);
    setCurrentLastKey("");
    setLastKeys({});
    setPage(0);
    await fetchReceivers({
      page: 0,
      limit: rowPerPage,
      lastkey: "",
      ...filters,
    });
    setIsLoading(false);
    setIsFilterLoading(false);
  };

  const handleSearchClear = () => {
    handleResetTable();
  };

  const handleResetTable = async () => {
    localStorage.removeItem("merchantIdToFilterReceiver");
    localStorage.removeItem("subaccountIdToFilterReceiver");
    setIsLoading(true);
    setIsFilterLoading(true);
    setFilters(initialFilters);
    setLastKeys([]);
    setCurrentLastKey("");
    setPage(0);
    await fetchReceivers({
      page: 0,
      limit: rowPerPage,
      lastkey: "",
      ...initialFilters,
    });
    setIsLoading(false);
    setIsFilterLoading(false);
  };

  const triggerReloadReceiverList = async () => {
    setIsLoading(true);
    setIsFilterLoading(true);
    await fetchReceivers({
      page: 0,
      limit: rowPerPage,
      lastkey: "",
      ...initialFilters,
    });
    setIsLoading(false);
    setIsFilterLoading(false);
  };

  return (
    <>
      <ReceiverToolbar
        userPermissions={userPermissions}
        isLoading={isLoading || isFilterLoading}
        onToggleFilters={() => setShowFilters((p) => !p)}
        onRefresh={triggerReloadReceiverList}
        onNewRecevier={handleClickCreate}
      />

      <ReceiverSearch
        showFilters={showFilters}
        isLoading={isLoading}
        filters={filters}
        setFilters={setFilters}
        handleSearchSubmit={handleSearchSubmit}
        handleSearchClear={handleSearchClear}
        isFilterLoading={isFilterLoading}
      />
      <ReceiverList
        isLoading={isLoading}
        page={page}
        pageSize={pageSize}
        rowCount={rowCount}
        tableData={isLoading ? [] : tableData}
        setPaginationModel={setPaginationModel}
        currentLastKey={currentLastKey}
        triggerReloadList={triggerReloadReceiverList}
      />
    </>
  );
};

export default Receiver;
